import {Component, computed, Input, signal} from '@angular/core';
import {NgIf, SlicePipe} from "@angular/common";
import {MatIcon} from "@angular/material/icon";
import {MatIconButton} from "@angular/material/button";
import {MatDivider} from "@angular/material/divider";
import {IBroadcastNotification} from "../../interfaces/notifications/IBroadcastNotification";
import {BroadcastNotificationTypes} from "../../enum/BroadcastNotificationTypes";
import {BroadcastNotificationScopeTypes} from "../../enum/BroadcastNotificationScopeTypes";
import {
  BroadcastNotificationBrowserPreviewComponent
} from "../broadcast-notification-browser-preview/broadcast-notification-browser-preview.component";

@Component({
  selector: 'app-broadcast-notification-preview',
  templateUrl: './broadcast-notification-preview.component.html',
  standalone: true,
  imports: [NgIf, MatIcon, BroadcastNotificationBrowserPreviewComponent],
  styleUrls: ['./broadcast-notification-preview.component.css']
})
export class BroadcastNotificationPreviewComponent {
  @Input() set notification(notification: IBroadcastNotification | Partial<IBroadcastNotification>) {
    this.notificationSignal.set(notification);
  }
  @Input() gameName: string = '';

  BroadcastNotificationTypes = BroadcastNotificationTypes;

  // Signals
  notificationSignal = signal<IBroadcastNotification | Partial<IBroadcastNotification>>({});

  // Computed signals for derived state
  title = computed(() => this.notificationSignal()?.Title || '');
  message = computed(() => this.notificationSignal()?.Message || '');
  isDismissible = computed(() => this.notificationSignal()?.IsDismissible || false);

  displayTypeName = computed(() => {
    const notification = this.notificationSignal();
    switch (notification?.BroadcastNotificationTypeId) {
      case BroadcastNotificationTypes.Banner:
        return 'Banner';
      case BroadcastNotificationTypes.Notification:
        return 'Notification';
      case BroadcastNotificationTypes.TopStrip:
        return 'Top Strip';
      default:
        return 'Unknown';
    }
  });

  targetSummary = computed(() => {
    const notification = this.notificationSignal();
    if (notification?.BroadcastNotificationScopeTypeId === BroadcastNotificationScopeTypes.System) {
      return 'All Users (System-wide)';
    } else {
      return notification?.GameId ? `${this.gameName || 'Loading...'} Users Only` : 'Game-Specific (Game not specified)';
    }
  });

  colorSummary = computed(() => {
    const notification = this.notificationSignal();
    const bgColor = notification?.BackgroundColor;
    const textColor = notification?.TextColor;

    if (!bgColor && !textColor) {
      return 'Default colors';
    }

    const parts = [];
    if (bgColor) {
      parts.push(`Background: ${bgColor}`);
    }
    if (textColor) {
      parts.push(`Text: ${textColor}`);
    }

    return parts.join(', ');
  });

  scheduleSummary = computed(() => {
    const notification = this.notificationSignal();
    if (!notification?.StartDate || !notification?.EndDate) {
      return 'Not scheduled';
    }

    const startDate = new Date(notification.StartDate);
    const endDate = new Date(notification.EndDate);

    const formatDate = (date: Date) => {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  });
}
